/*
 * Whether an email follows the application's theme or keeps its own light page (DESIGN.md, the Token
 * Pair Rule). Most lead replies are plain mail: words, links and quoted history, coloured at most
 * black on white. Those read in the theme's own ink, so dark mode is dark. Mail its author styled
 * (any other colour, a table layout or a picture) keeps the light page it was designed on, unfiltered.
 * The caller passes what is on screen: the reply alone while its quoted history is folded, and only
 * the pictures actually shown count, since a held-back signature image draws nothing of the author's.
 *
 * The decision is conservative: anything this cannot read as neutral, an unknown colour word or a
 * `var()`, counts as styled. It runs on the sanitizer's output (`sanitizeInboundEmailHtml`), never on
 * raw mail, and `withoutNeutralColours` only removes declarations and attributes this has already read
 * as neutral, so it cannot add markup or loosen anything the sanitizer stripped. That output quotes
 * every attribute and escapes `<` in text, so `<` and a letter start a real tag and a tag ends at its
 * first `>`, as `withImagePlaceholders` relies on too. Deciding at render rather than at projection
 * covers snapshots stored before this existed and leaves the stored HTML other readers use unchanged.
 */

export type EmailStyling = "plain" | "styled";

type Rgb = readonly [number, number, number];

export const TAG = /<(\/?)([a-z][a-z0-9]*)\b([^>]*)>/gi;
export const ATTRIBUTE = /\s([^\s="]+)(?:="([^"]*)")?/g;
/** A table lays the email out: it keeps that page, as a picture on screen does (`pictureShown`). */
const LAYOUT_TAGS = new Set(["table", "thead", "tbody", "tfoot", "tr", "td", "th"]);
const VALUE_TOKEN =
  /(?:rgba?|hsla?|hwb|lab|lch|oklab|oklch|color)\([^)]*\)|#[0-9a-f]+|[a-z-]+|\S+/gi;

const NAMED: Readonly<Record<string, Rgb>> = {
  black: [0, 0, 0],
  windowtext: [0, 0, 0],
  dimgray: [105, 105, 105],
  dimgrey: [105, 105, 105],
  gray: [128, 128, 128],
  grey: [128, 128, 128],
  darkgray: [169, 169, 169],
  darkgrey: [169, 169, 169],
  silver: [192, 192, 192],
  lightgray: [211, 211, 211],
  lightgrey: [211, 211, 211],
  gainsboro: [220, 220, 220],
  whitesmoke: [245, 245, 245],
  white: [255, 255, 255],
  window: [255, 255, 255],
};
/** Words that give an element no colour of its own. */
export const NO_COLOUR = new Set(["inherit", "initial", "unset", "revert", "currentcolor", "auto"]);
export const NO_BACKGROUND = new Set([
  ...NO_COLOUR,
  "none",
  "transparent",
  "repeat",
  "no-repeat",
  "scroll",
]);
export const BORDER_WORDS = new Set([
  ...NO_COLOUR,
  "none",
  "hidden",
  "dotted",
  "dashed",
  "solid",
  "double",
  "groove",
  "ridge",
  "inset",
  "outset",
  "thin",
  "medium",
  "thick",
  "transparent",
]);
export const DECORATION_WORDS = new Set([
  ...NO_COLOUR,
  "none",
  "underline",
  "overline",
  "line-through",
  "solid",
  "double",
  "dotted",
  "dashed",
  "wavy",
]);

function channel(text: string): number | null {
  const value = text.trim();
  if (value.endsWith("%")) return Math.round((Number(value.slice(0, -1)) / 100) * 255);
  return value === "" ? null : Number(value);
}

/** A colour this can read, or null: an unreadable colour is the author's and keeps the light page. */
function rgbOf(token: string): Rgb | null {
  const word = token.toLowerCase();
  const named = NAMED[word];
  if (named) return named;
  const hex = /^#([0-9a-f]{3,8})$/.exec(word)?.[1];
  if (hex && [3, 4, 6, 8].includes(hex.length)) {
    const short = hex.length <= 4;
    const at = (index: number) =>
      short
        ? Number.parseInt(`${hex[index]}${hex[index]}`, 16)
        : Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16);
    return [at(0), at(1), at(2)];
  }
  const rgb = /^rgba?\(([^)]*)\)$/.exec(word)?.[1];
  if (rgb) {
    const parts = rgb
      .split(/[\s,/]+/)
      .filter(Boolean)
      .slice(0, 3)
      .map(channel);
    const [r, g, b] = parts;
    if (
      parts.length === 3 &&
      r != null &&
      g != null &&
      b != null &&
      [r, g, b].every(Number.isFinite)
    )
      return [r, g, b];
  }
  return null;
}

const achromatic = ([r, g, b]: Rgb): boolean => Math.max(r, g, b) - Math.min(r, g, b) <= 24;
/** Black and the dark greys mail clients write for ordinary text. White text means a dark ground. */
const neutralText = (rgb: Rgb): boolean => achromatic(rgb) && Math.max(...rgb) <= 170;
/** White and the near-whites mail clients write as the page behind ordinary text. */
const neutralGround = (rgb: Rgb): boolean => achromatic(rgb) && Math.min(...rgb) >= 232;

function tokens(value: string): string[] {
  return (value.replace(/!\s*important\s*$/i, "").match(VALUE_TOKEN) ?? []).filter(
    (token) => token !== ",",
  );
}

/** Each of the value's words is one this allows, or a colour that passes the test. */
function neutralValue(
  value: string,
  allowed: ReadonlySet<string>,
  colour: (rgb: Rgb) => boolean,
): boolean {
  return tokens(value).every((token) => {
    if (allowed.has(token.toLowerCase()) || /^[-+]?[\d.]+[a-z%]*$/i.test(token)) return true;
    const rgb = rgbOf(token);
    return rgb !== null && colour(rgb);
  });
}

function neutralTextValue(value: string): boolean {
  const [only, ...rest] = tokens(value);
  if (only === undefined || rest.length > 0) return false;
  if (NO_COLOUR.has(only.toLowerCase())) return true;
  const rgb = rgbOf(only);
  return rgb !== null && neutralText(rgb);
}

export const entityText = (value: string): string =>
  value.replaceAll("&quot;", '"').replaceAll("&amp;", "&");
/** A `;` that ends a declaration, not one that ends an entity such as `&quot;` inside a font name. */
export const DECLARATION_END = /(?<!&(?:[a-z]+|#\d+|#x[0-9a-f]+));/i;

function declarations(style: string): { property: string; value: string }[] {
  return style
    .split(DECLARATION_END)
    .map(entityText)
    .flatMap((part) => {
      const colon = part.indexOf(":");
      if (colon < 0) return [];
      return [
        {
          property: part.slice(0, colon).trim().toLowerCase(),
          value: part.slice(colon + 1).trim(),
        },
      ];
    });
}

/** True when the declaration carries no colour of the author's. A link's own colour is the client's. */
function neutralDeclaration(property: string, value: string, inLink: boolean): boolean {
  if (property === "color") return inLink || neutralTextValue(value);
  if (property === "background" || property === "background-color")
    return neutralValue(value, NO_BACKGROUND, neutralGround);
  if (
    property.startsWith("border") &&
    !property.endsWith("radius") &&
    !property.endsWith("collapse")
  )
    return neutralValue(value, BORDER_WORDS, achromatic);
  if (property === "text-decoration") return neutralValue(value, DECORATION_WORDS, achromatic);
  return true;
}

function attributesOf(text: string): { name: string; value: string }[] {
  return [...text.matchAll(ATTRIBUTE)].map((match) => ({
    name: (match[1] ?? "").toLowerCase(),
    value: match[2] ?? "",
  }));
}

function neutralAttribute(attribute: { name: string; value: string }, inLink: boolean): boolean {
  if (attribute.name === "style")
    return declarations(attribute.value).every((declaration) =>
      neutralDeclaration(declaration.property, declaration.value, inLink),
    );
  if (attribute.name === "color") return inLink || neutralTextValue(entityText(attribute.value));
  if (attribute.name === "bgcolor")
    return neutralValue(entityText(attribute.value), NO_BACKGROUND, neutralGround);
  return true;
}

export type StylingOptions = {
  /**
   * Whether the picture behind an `<img>` tag's attributes is on screen, or on its way. A picture
   * held back, hidden, failed or drawn as a stand-in sets no page. Unsaid, every picture counts.
   */
  readonly pictureShown?: (attributes: string) => boolean;
};

/** Plain when every colour, ground and rule in the email is neutral and nothing lays it out. */
export function emailStyling(sanitizedHtml: string, options: StylingOptions = {}): EmailStyling {
  const pictureShown = options.pictureShown ?? (() => true);
  let linkDepth = 0;
  for (const [, closing, rawName = "", attributes = ""] of sanitizedHtml.matchAll(TAG)) {
    const name = rawName.toLowerCase();
    if (name === "a") linkDepth = Math.max(0, linkDepth + (closing ? -1 : 1));
    if (closing) continue;
    if (LAYOUT_TAGS.has(name) || (name === "img" && pictureShown(attributes))) return "styled";
    const inLink = linkDepth > 0;
    if (!attributesOf(attributes).every((attribute) => neutralAttribute(attribute, inLink)))
      return "styled";
  }
  return "plain";
}

const THEMED_PROPERTIES = new Set(["color", "background", "background-color"]);

/**
 * Plain mail without its neutral text colours and page grounds, so the theme's ink and sheet show.
 * Only for mail `emailStyling` called plain; in styled mail a white cell can sit on a coloured one.
 */
export function withoutNeutralColours(sanitizedHtml: string): string {
  return sanitizedHtml.replaceAll(TAG, (tag, closing: string, name: string, attributes: string) => {
    if (closing) return tag;
    const kept = attributes.replaceAll(ATTRIBUTE, (attribute, rawName: string, value?: string) => {
      const attributeName = rawName.toLowerCase();
      if (attributeName === "color" || attributeName === "bgcolor") return "";
      if (attributeName !== "style" || value === undefined) return attribute;
      const rest = value
        .split(DECLARATION_END)
        .filter(
          (part) => !THEMED_PROPERTIES.has(part.slice(0, part.indexOf(":")).trim().toLowerCase()),
        )
        .join(";");
      return rest.trim() === "" ? "" : ` style="${rest}"`;
    });
    return `<${name}${kept}>`;
  });
}
